import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = ["5K", "10K", "Half-Marathon", "Not sure yet"];
const EXPERIENCE = ["Brand new to running", "Returning after a break", "Regular runner", "Competitive runner"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    goal: "",
    experience: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const g = e?.detail?.goal || "";
      if (!g) return;
      const match = GOALS.find((x) => g.toLowerCase().includes(x.toLowerCase().split("-")[0])) || "";
      if (match) setForm((f) => ({ ...f, goal: match }));
    };
    window.addEventListener("prefill-contact", handler);
    return () => window.removeEventListener("prefill-contact", handler);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        goal: form.goal || null,
        experience: form.experience || null,
        message: form.message.trim(),
      });
      if (res.data?.status === "received") {
        setSent(true);
        toast.success("Application received. Ashley will be in touch within 24 hours.");
        setForm({ name: "", email: "", goal: "", experience: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Couldn't send your message. Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI 422 validation error shape
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : "";
        const fieldLabel =
          { name: "Name", email: "Email", goal: "Goal", experience: "Experience", message: "Message" }[field] || "Form";
        msg = `${fieldLabel}: ${first?.msg || "is invalid."}`;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 border-b border-white/10 bg-[#080808]"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-brand-red text-xs tracking-widest">07</span>
              <span className="label-eyebrow">Apply</span>
            </div>
            <h2 className="font-display uppercase text-white text-5xl lg:text-7xl leading-[0.9] tracking-tight">
              Let's build your <span className="text-brand-red">next race.</span>
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed max-w-md">
              Tell me about you, your running and your goal. I respond personally within
              24 hours.
            </p>

            <div className="mt-10 space-y-4 text-sm text-white/65">
              <ContactLine label="Email" value="ashley@precisionrunning.com.au" />
              <ContactLine
                label="Instagram"
                value={
                  <a
                    href="https://instagram.com/ashleykempson"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-instagram"
                    className="hover:text-brand-red"
                  >
                    @ashleykempson
                  </a>
                }
              />
              <ContactLine label="Based" value="Coaching athletes worldwide" />
              <ContactLine label="Response" value="< 24 hours, every day" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[#0e0e10] border border-white/10 p-6 lg:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Your name" id="name">
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Hannah Bell"
                    className="bg-white/5 border border-white/10 rounded-none text-white placeholder:text-white/30 focus-visible:border-brand-red focus-visible:ring-0 h-12"
                  />
                </Field>
                <Field label="Email" id="email">
                  <Input
                    id="email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@runner.com"
                    className="bg-white/5 border border-white/10 rounded-none text-white placeholder:text-white/30 focus-visible:border-brand-red focus-visible:ring-0 h-12"
                  />
                </Field>

                <Field label="Goal distance" id="goal">
                  <Select value={form.goal} onValueChange={(v) => update("goal", v)}>
                    <SelectTrigger
                      data-testid="contact-select-goal"
                      className="bg-white/5 border border-white/10 rounded-none text-white h-12 focus:ring-0 focus:border-brand-red"
                    >
                      <SelectValue placeholder="Select a distance" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111111] border border-white/10 rounded-none text-white">
                      {GOALS.map((g) => (
                        <SelectItem key={g} value={g} data-testid={`contact-goal-${g}`}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Experience" id="experience">
                  <Select
                    value={form.experience}
                    onValueChange={(v) => update("experience", v)}
                  >
                    <SelectTrigger
                      data-testid="contact-select-experience"
                      className="bg-white/5 border border-white/10 rounded-none text-white h-12 focus:ring-0 focus:border-brand-red"
                    >
                      <SelectValue placeholder="How would you describe yourself?" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111111] border border-white/10 rounded-none text-white">
                      {EXPERIENCE.map((e) => (
                        <SelectItem key={e} value={e}>
                          {e}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="md:col-span-2">
                  <Field label="Tell Ashley about your goal" id="message">
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      rows={5}
                      placeholder="What are you training for, and what does success look like?"
                      className="bg-white/5 border border-white/10 rounded-none text-white placeholder:text-white/30 focus-visible:border-brand-red focus-visible:ring-0 resize-none"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-white/40 max-w-sm leading-relaxed">
                  By submitting you agree to be contacted by Precision Running about your enquiry.
                  Your data stays private.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Submit application <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>

              {sent && (
                <div
                  data-testid="contact-success"
                  className="mt-6 border border-brand-red/40 bg-brand-red/10 p-4 text-sm text-white"
                >
                  Application received — Ashley will be in touch within 24 hours.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label
        htmlFor={id}
        className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

function ContactLine({ label, value }) {
  return (
    <div className="flex items-baseline gap-4 border-b border-white/10 pb-3">
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold min-w-[80px]">
        {label}
      </span>
      <span className="text-white">{value}</span>
    </div>
  );
}
